import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';

// Theme colors using the app SCSS variables (approximated in code)
// You can tweak these to exactly match variables in styles if needed.
const primaryBlue = '#4477AA';
const subtleGrey = '#7A7A7A';
const black = '#333333';
const green = '#228833';
const orange = '#EE7733';
const purple = '#AA3377';
const red = '#CC3311';
const teal = '#66CCEE';
const amber = '#CCBB44';
const pink = '#EE6677';
const brown = '#6d4c41';

// Semantic color aliases (improves readability and consistency)
const colorBoolean = primaryBlue; // AND, OR, &&, ||
const colorProximitySentence = teal; // NEAR/ONEAR/NOTNEAR and WITH/NOTWITH/NEARWITH
const colorExclusion = red; // NOT, EXCLUDE
const colorOrder = amber; // FOLLOW, FOLLOWWITH
const colorString = green;
const colorNumber = orange;
const colorModifier = orange; // + - ~
const colorEntity = pink; // PERSON, COMPANY, etc.
const colorWildcard = brown; // *term, term*
const colorMeta = purple; // {meta}
const colorTerm = black; // regular terms
const colorOperator = black; // generic symbol operators
const colorComment = subtleGrey; // comments
const colorBracket = subtleGrey; // brackets

// Custom token types for LexQuery syntax
const customTags = {
  boolean: t.definitionKeyword,
  proximity: t.moduleKeyword,
  sentence: t.controlKeyword,
  exclusion: t.operatorKeyword,
  order: t.keyword,
  entity: t.typeName,
  term: t.variableName,
  wildcard: t.regexp,
  meta: t.meta,
  modifier: t.modifier,
};

export const lexQueryHighlightStyle = HighlightStyle.define([
  // Standard tokens
  { tag: t.keyword, color: colorProximitySentence, fontWeight: 'bold' },
  { tag: t.string, color: colorString },
  { tag: t.number, color: colorNumber },
  { tag: t.atom, color: colorBoolean, fontWeight: 'bold' }, // Caret references (^...)
  { tag: t.operator, color: colorOperator, fontWeight: 'bold' },
  { tag: t.comment, color: colorComment, fontStyle: 'italic' },
  { tag: t.bracket, color: colorBracket },
  { tag: t.typeName, color: colorEntity }, // Entity types

  // Custom LexQuery tokens
  { tag: customTags.boolean, color: colorBoolean, fontWeight: 'bold' }, // AND, OR
  { tag: customTags.proximity, color: colorProximitySentence, fontWeight: 'bold' }, // NEAR, ONEAR, NOTNEAR
  { tag: customTags.sentence, color: colorProximitySentence, fontWeight: 'bold' }, // WITH, NOTWITH, NEARWITH
  { tag: customTags.exclusion, color: colorExclusion, fontWeight: 'bold' }, // NOT, EXCLUDE
  { tag: customTags.order, color: colorOrder, fontWeight: 'bold' }, // FOLLOW, FOLLOWWITH
  { tag: customTags.entity, color: colorEntity }, // PERSON, PLACE, etc.
  { tag: customTags.term, color: colorTerm }, // Regular terms
  { tag: customTags.wildcard, color: colorWildcard }, // *term, term*
  { tag: customTags.meta, color: colorMeta, fontStyle: 'italic' }, // {meta}
  { tag: customTags.modifier, color: colorModifier, fontWeight: 'bold' }, // +term, -term, ~term
]);

export const lexQueryTheme = EditorView.theme(
  {
    '&': {
      color: black,
      backgroundColor: 'transparent !important',
      borderRadius: '4px',
      border: '1px solid #e0e0e0',
      backgroundImage:
        'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFcAAAAyCAYAAAAp8UeFAAAHvklEQVR42s2b63bcNgyEQZCSHCdt2vd/0tWF7I+Q6XgMXiTtuvU5Pl57ZQKkKHzEAOtF5KeIJBGJ8uvL599FRFREZhFx8DeXv8trn68RuGaC8TRfo3SNp9dlDDHedyLyTUTeRWStXKPZrjtpZxaRw5hPqozRs1N8/enzIiQRWcCgy4MUA0f+XWliDhyL8Lfyvx7ei/Ae3iQFHyw7U/59pQVIMEEPEz0G7XiwdRjzSfC3UTtz9vchIntxvry5iMgfIhJoEflOz2CQr3F5h/HfeFe+GTdLaKcu9L8LTeQb/R/7GgbsfKedyNdoHsN31uRPWrfZ5wsj/NzzRQHuToIdU3ahwnsKPxXCjJITuOsi7XLc7SG/v5GdALs7wf8JjTFiB5+QvTEfRyGOfX3Lrx8wxyQi3sNq46O7QahQiCsRFgqddjBouVEHOKDgXAQHD9gJCr5sMKkEdjwsarG/ww3BMHBU7OBjXnzdyY7SfCxf5/z6ATccrwlKuwC/jhznnPF4CgVzhhVf4xp2EixcBActO75iZ8/fM9zAs2OMzKdslgXWJ9XG8PQoOAMA5fGcsvORgv0doBXyHrCwfLJAOwo71QLNkb8n2Pl6EWiR7OCibtkPaz4Kc/0NNAze2gju3zOwekALDaCFPI5vjPFmgGY5AZqyGEvH1x7QfIb8YtxMnA/b+QQ0aQDAwc6JMFg8CbQZ4qoYEEHbRwNojuK3EHwd7VALSgq+MNDKzfT58T8qdpADrgW0GmgcAS1lhzztJmkAzcPNOQbsWEALBDSlMKUG0Eq4CLAQWvEVQ9WU57gZJwZtgPO3r9oBTQ9WO8TjqXINx8R0EYpiZEUWOF3FxkbJkgU9B2f41YBrIj5ZfsQa0M5kTgiAAqM3ShXLgu8XMqcrQBvJ0CL5pnTsfMB13oB8athpAq2XOQmcGmoACCLydx7nToa23ATaSIY2ichfOdPTGxlasXMLaL0MLZAOwAKIM+y8CmicobGdCcbbK9DzN+yYGVoNNI5iUKTMyYOjPse4A8SM1MmcXgU0toOq1yO/v8FOxlASyc7TgeYaAMBJHcY1CcCwGI/TK4AmDbDyKYBBtFUkRwto8gygiQEaByFgJ00BH2M8JWwQS1nafDXQCidWyOI8AcjDCSjCLk8ngObuAm3JAHAdubAmOaK06V8MNEsKPJOhobSprwQa6gD7DclRQdqcwL4zxqgBrQcabUiBLclRDKAlWp+etPkBaNMA0AKlrHwTdEByZAA4GM+SNluSY6wAzcMNewxmgig5Ks0nkrSpBvSaQHMdKTBAnLojOdYyGpQ254602ZILPdTD1hdlggdIm74jbTp8vDwF5ZYUeLWGJpWsh6XNyXgcYwVoJQTEhhTYkxzZjiU5npU2TaB979TQehlaAVq4kaGpiPwwwLkYUuBbQwocyQTv1tA0+1UFWoJF3iv1oq+qoSk8EQdJmwHkziIF7oOZk14EGitibAdjLYYK78H5vZOhtWpoI0ATGHs0Q8OMb4Ey+2bU2UYztCtA0wFAs7TplGLRVQCcqaFdGSPCeTI1QNIC52iWNzof6Uib7xjEp07mNNoUYmVosVItHrHzRlLgBn9LFyRHaQCtVUMbtTNhoXWiTOO9k/V8BdAc1Oq0ArSQs6/5SU0hckNy9NnXqQY0PGYo5dWJ7nINaN6o958FWin27aBaWRka1r5myvLOAm0j30eBJqCxHLReVclxhxOEN2JfDWjxBtAC7MIH1fVaGdoOp4qJYDgKtKPSFNID2gSnGldrCqkFZ+5UeQXQBIRrSwocbdZYQT/2LwRahBPBXoHrB8nxaGROST62DKUbQOMMzZIC9abkuELfQzQALWTnDNAm8KHWFOJgJ5+SHIvTPcmx1xQyZRhNL5Qci689aXMEaN/uNIWkEwDAvFpOZmgsBaaGnbs1NPa1Jm32gBZAIh1pCtG7TSH4aE0y1uVY4uqoFPisGlpP2rSA5qTecWn5agK6BzSpgAyD+wFaqhnYoSZ1Vwr8CmlTQbrcO3ZaX0NAEyMbYaAlyquFoLKK3SPby9CeVUPThrSJmkCAE0CrKUQadi4DrdSlWhmah0YL9z9vClH59YGbHx1J8VZTyAjQepJjmXwAKTDQI3omc3p1U4gDUf6RfcdYfrUp5ClAi2J3Ba6UOXGo+K+bQrjjssitG2SJzshaLwMtXgRagUNpYYoVkMSBLM+9GGiJZMvduG6DRZ4qc04DMPtQQxOjEtACmhO7K1AbNbQDEggZyJwscFpAGwENhoBeUwh3bWolhe8BTYVKxQEWrSUn/uhcM5KhvUu/+eQu0Lzhi+VrK0PrZZNDQKs9cpYUuFYgMVpD4/NxenJTiMCNqdUEUf1qZWjppLT5qSkkUZbCwkbZMSuVnu80hfSkzRbQeqCZSAh6huR4VtoM2gHAlLf72smuWgE+VV7XpE25Ab2WFDgyhnSuKbs4GuGzCjR+tIoUuMFg3kgcWKLTwRqanJQ2W00hAsenfaApRC42hbCvK1SlE0HtE9BGgneJO+ELamitD1YjjOYnNYVcraGhtKkW0EqVVeDx733I2NH581k1NNxNLG0i0IJ8/NjVaOZ0tYZ2Vtr0Xv7tPV3hkWp9EFkgS/J0vosngTaSoaG06WHi+xObQkaAdlbanP8B2+2l0f90LmUAAAAASUVORK5CYII=) !important',
      backgroundRepeat: 'repeat !important',
    },
    '.cm-scroller': {
      backgroundColor: 'transparent !important',
      backgroundImage:
        'url(data:image/png;base64,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) !important',
      backgroundRepeat: 'repeat !important',
    },
    '.cm-content': {
      fontSize: '16px',
      lineHeight: '1.5',
      padding: '8px 0',
    },
    '.cm-gutters': {
      backgroundColor: 'rgba(242, 247, 255, 0.7)',
      color: subtleGrey,
      borderRightColor: '#e0e0e0',
      fontSize: '14px',
    },
    '.cm-activeLine': {
      backgroundColor: 'rgba(18, 116, 209, 0.06)',
    },
    '.cm-activeLineGutter': {
      backgroundColor: 'rgba(18, 116, 209, 0.1)',
      color: primaryBlue,
      fontWeight: '500',
    },
    '.cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection': {
      backgroundColor: 'rgba(18, 116, 209, 0.2)',
    },
    '.cm-cursor': {
      borderLeftColor: black,
      borderLeftWidth: '2px',
    },
    '.cm-matchingBracket': {
      backgroundColor: 'rgba(46, 125, 50, 0.2)',
      outline: '1px solid #2e7d32',
    },
    '.cm-nonmatchingBracket': {
      backgroundColor: 'rgba(198, 40, 40, 0.2)',
      outline: '1px solid #c62828',
    },
    // Search hit highlight (used by taxonomy search occurrences)
    '.cm-lexquery-search-hit': {
      backgroundColor: 'rgba(255, 213, 0, 0.35)',
      borderRadius: '2px',
      boxShadow: '0 0 0 1px rgba(255, 213, 0, 0.6) inset',
    },
    // Specific token styling for LexQuery
    '.cm-keyword': {
      color: colorProximitySentence,
      fontWeight: 'bold',
    },
    '.cm-bracket': {
      color: colorBracket,
    },
    '.cm-def': {
      color: colorProximitySentence,
      fontWeight: 'bold',
    },
    // Sentence-level operators (WITH, NOTWITH, NEARWITH, ONEARWITH)
    '.cm-sentence': {
      color: colorProximitySentence,
      fontWeight: 'bold',
    },
    '.cm-builtin': {
      color: colorProximitySentence,
      fontWeight: 'bold',
    },
    '.cm-error': {
      color: colorExclusion,
      fontWeight: 'bold',
      backgroundColor: 'transparent',
      textDecoration: 'none',
    },
    '.cm-tag': {
      color: colorOrder,
      fontWeight: 'bold',
    },
    '.cm-atom': {
      color: colorBoolean,
      fontWeight: 'bold',
    }, // Caret references (^...)
    '.cm-type.cm-entity': {
      color: colorEntity,
    },
    '.cm-term': {
      color: colorTerm,
    },
    '.cm-wildcard': {
      color: colorWildcard,
      textDecoration: 'underline',
    },
    '.cm-meta': {
      color: colorMeta,
      fontStyle: 'italic',
    },
    '.cm-modifier': {
      color: colorModifier,
      fontWeight: 'bold',
    },
  },
  { dark: false }
);

export const lexQueryExtensions = [syntaxHighlighting(lexQueryHighlightStyle), lexQueryTheme];
